import React from 'react';
import { CandiBankUploadItem } from '../hooks/useCandiBankUpload';
import {
  ChevronDownIcons,
  XIcons,
  UploadIcons,
  RefreshListIcons,
} from '../../../icons/icons';
import { UsageMeter } from '../../../ui/usage_meter/UsageMeter';

interface CandiBankUploadWidgetProps {
  items: CandiBankUploadItem[];
  isOpen: boolean;
  isMinimized: boolean;
  onMinimizeToggle: () => void;
  onClose: () => void;
  onRetry: (id: string) => void;
  onRemove: (id: string) => void;
  onClearCompleted: () => void;
  formatFileSize: (bytes: number) => string;
  overallProgress: number;
  completedCount: number;
  duplicateCount?: number;
  totalCount: number;
  isUploading: boolean;
}

export const CandiBankUploadWidget: React.FC<CandiBankUploadWidgetProps> = ({
  items,
  isOpen,
  isMinimized,
  onMinimizeToggle,
  onClose,
  onRetry,
  onRemove,
  onClearCompleted,
  formatFileSize,
  overallProgress,
  completedCount,
  duplicateCount = 0,
  totalCount,
  isUploading,
}) => {
  if (!isOpen || totalCount === 0) return null;

  const failedCount = items.filter((i) => i.status === 'failed').length;
  const blockedCount = items.filter((i) => i.status === 'blocked').length;

  return (
    <div
      className={`fixed bottom-5 right-6 z-[9990] transition-all duration-300 ease-in-out font-sans ${
        isMinimized ? 'w-80' : 'w-[460px] max-w-[calc(100vw-3rem)]'
      }`}
    >
      <div className='bg-white rounded-2xl border border-slate-200/90 shadow-2xl shadow-slate-900/20 overflow-hidden flex flex-col'>
        
        {/* ── Google Drive-Style Dark Header ───────────────────────────────── */}
        <div
          onClick={isMinimized ? onMinimizeToggle : undefined}
          className={`flex items-center justify-between px-4 py-3 bg-slate-900 text-white select-none transition-colors ${
            isMinimized ? 'cursor-pointer hover:bg-slate-800' : ''
          }`}
        >
          <div className='flex items-center gap-3 min-w-0'>
            <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 backdrop-blur-xs'>
              {isUploading ? (
                <svg className='h-4 w-4 animate-spin text-emerald-400' fill='none' viewBox='0 0 24 24'>
                  <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                  <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                </svg>
              ) : (
                <UploadIcons className='h-4 w-4 text-emerald-400' />
              )}
            </div>

            <div className='min-w-0 flex-1'>
              <h4 className='text-xs font-bold tracking-tight text-white truncate'>
                {isUploading
                  ? `Uploading ${totalCount - completedCount - duplicateCount} of ${totalCount} CVs...`
                  : `Uploaded ${completedCount + duplicateCount} of ${totalCount} CVs`}
              </h4>

              {isMinimized && (
                <div className='flex items-center gap-2 mt-0.5'>
                  <div className='h-1.5 w-24 overflow-hidden rounded-full bg-white/20'>
                    <div
                      className='h-full bg-emerald-400 transition-all duration-300 rounded-full'
                      style={{ width: `${overallProgress}%` }}
                    />
                  </div>
                  <span className='text-[10px] font-mono font-bold text-slate-300'>
                    {overallProgress}%
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className='flex items-center gap-1 shrink-0 ml-2'>
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                onMinimizeToggle();
              }}
              className='p-1.5 rounded-lg text-slate-400 hover:bg-white/10 hover:text-white transition-colors'
              title={isMinimized ? 'Expand' : 'Minimize'}
            >
              <ChevronDownIcons className={`h-4 w-4 transition-transform ${isMinimized ? 'rotate-180' : ''}`} />
            </button>

            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className='p-1.5 rounded-lg text-slate-400 hover:bg-white/10 hover:text-white transition-colors'
              title='Close'
            >
              <XIcons className='h-4 w-4' />
            </button>
          </div>
        </div>

        {/* ── Expanded Content Area ────────────────────────────────────────── */}
        {!isMinimized && (
          <>
            {/* Overall Progress Bar & Clean SaaS Metric Badges */}
            <div className='px-4 py-3 bg-slate-50 border-b border-slate-200/70 space-y-2.5'>
              <div className='flex items-center justify-between text-[11px] font-semibold text-slate-600'>
                <span>Upload Progress</span>
                <span className='font-mono font-bold text-slate-900'>
                  {overallProgress}% ({completedCount + duplicateCount}/{totalCount} Processed)
                </span>
              </div>

              <UsageMeter used={overallProgress} limit={100} tone='ok' />

              <div className='flex items-center justify-between text-[11px] pt-0.5'>
                <div className='flex items-center gap-1.5'>
                  <span className='px-2.5 py-0.5 rounded-full font-semibold text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200'>
                    {completedCount} Done
                  </span>

                  {duplicateCount > 0 && (
                    <span className='px-2.5 py-0.5 rounded-full font-semibold text-[10px] bg-amber-50 text-amber-700 border border-amber-200'>
                      {duplicateCount} Duplicate
                    </span>
                  )}

                  {isUploading && (
                    <span className='px-2.5 py-0.5 rounded-full font-semibold text-[10px] bg-blue-50 text-blue-700 border border-blue-200 animate-pulse'>
                      Processing...
                    </span>
                  )}

                  {failedCount > 0 && (
                    <span className='px-2.5 py-0.5 rounded-full font-semibold text-[10px] bg-rose-50 text-rose-700 border border-rose-200'>
                      {failedCount} Failed
                    </span>
                  )}

                  {blockedCount > 0 && (
                    <span
                      className='px-2.5 py-0.5 rounded-full font-semibold text-[10px] bg-rose-50 text-rose-700 border border-rose-200'
                      title='Monthly CV credit limit reached'
                    >
                      {blockedCount} No credits
                    </span>
                  )}
                </div>

                {(completedCount > 0 || duplicateCount > 0 || blockedCount > 0) && (
                  <button
                    type='button'
                    onClick={onClearCompleted}
                    className='text-slate-500 hover:text-slate-900 font-semibold text-[11px] transition-colors'
                  >
                    Clear completed
                  </button>
                )}
              </div>
            </div>

            {/* Scrollable File List */}
            <div className='max-h-72 overflow-y-auto divide-y divide-slate-100 p-1.5 space-y-0.5 bg-white'>
              {items.map((item) => (
                <div
                  key={item.id}
                  className='group flex items-center justify-between gap-3 p-2.5 rounded-xl hover:bg-slate-50/80 transition-colors text-xs'
                >
                  {/* File Format Badge Icon */}
                  <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600 border border-slate-200 font-bold text-[10px] uppercase'>
                    {item.name.split('.').pop()?.substring(0, 4) || 'CV'}
                  </div>

                  {/* File Info & Status */}
                  <div className='min-w-0 flex-1 space-y-1'>
                    <div className='flex items-center justify-between gap-2'>
                      <p className='font-medium text-slate-900 truncate text-[11px]' title={item.name}>
                        {item.name}
                      </p>
                      <span className='text-[10px] text-slate-400 shrink-0 font-normal'>
                        {formatFileSize(item.size)}
                      </span>
                    </div>

                    {/* Progress Bar, Duplicate Notice, or Error Text */}
                    {item.status === 'duplicate' ? (
                      <p className='text-[10px] text-amber-700 font-medium truncate flex items-center gap-1'>
                        <span className='h-1.5 w-1.5 rounded-full bg-amber-500 inline-block' />
                        {item.error || 'Duplicate CV — Already in Candi Bank'}
                      </p>
                    ) : item.status === 'failed' ? (
                      <p className='text-[10px] text-rose-600 font-medium truncate'>
                        {item.error || 'Upload error'}
                      </p>
                    ) : item.status === 'blocked' ? (
                      <p className='text-[10px] text-rose-600 font-medium truncate flex items-center gap-1'>
                        <span className='h-1.5 w-1.5 rounded-full bg-rose-500 inline-block' />
                        {item.error || 'Monthly CV credit limit reached'}
                      </p>
                    ) : (
                      <div className='flex items-center gap-2'>
                        <div className='h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100'>
                          <div
                            className={`h-full transition-all duration-200 rounded-full ${
                              item.status === 'completed'
                                ? 'bg-emerald-500'
                                : 'bg-emerald-600'
                            }`}
                            style={{ width: `${item.progress}%` }}
                          />
                        </div>
                        <span className='text-[10px] font-mono text-slate-400 w-8 text-right'>
                          {item.progress}%
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Action Badges & Icons */}
                  <div className='flex items-center gap-1 shrink-0'>
                    {item.status === 'completed' && (
                      <span className='flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 font-bold text-[10px]' title='Uploaded'>
                        ✓
                      </span>
                    )}

                    {item.status === 'duplicate' && (
                      <span className='flex px-2 py-0.5 items-center justify-center rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold text-[10px]' title='Already in bank'>
                        Duplicate
                      </span>
                    )}

                    {item.status === 'blocked' && (
                      <span className='flex px-2 py-0.5 items-center justify-center rounded-full bg-rose-50 text-rose-700 border border-rose-200 font-semibold text-[10px]' title='No credits left this month'>
                        Blocked
                      </span>
                    )}

                    {(item.status === 'failed' || item.status === 'blocked') && (
                      <button
                        type='button'
                        onClick={() => onRetry(item.id)}
                        className='p-1 text-slate-400 hover:text-emerald-600 rounded-lg hover:bg-slate-100 transition-colors'
                        title={item.status === 'blocked' ? 'Retry (once credits are available)' : 'Retry'}
                      >
                        <RefreshListIcons className='h-3.5 w-3.5' />
                      </button>
                    )}

                    <button
                      type='button'
                      onClick={() => onRemove(item.id)}
                      className='p-1 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-slate-100 transition-colors opacity-0 group-hover:opacity-100'
                      title='Remove'
                    >
                      <XIcons className='h-3.5 w-3.5' />
                    </button>
                  </div>
                </div>
              ))}
            </div>

          </>
        )}

      </div>
    </div>
  );
};
